import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/index.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetupPassword from "./pages/SetupPassword";
import Signup from "./pages/Signup";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import LeadInbox from "./pages/LeadInbox";
import Billing from "./pages/Billing";
import LeadDetail from "./pages/LeadDetail";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Deals from "./pages/Deals";
import DealDetail from "./pages/DealDetail";
import Moves from "./pages/Moves";
import Insights from "./pages/Insights";
import Settings from "./pages/Settings";
import SuperadminLogin from "./pages/SuperadminLogin";
import SuperadminDashboard from "./pages/SuperadminDashboard";
import Setup from "./pages/Setup";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Deliverables from "./pages/Deliverables";
import OnboardingWizard from "./pages/OnboardingWizard";
import Proposals from "./pages/Proposals";
import ProposalBuilder from "./pages/ProposalBuilder";
import SigningPageBuilder from "./pages/SigningPageBuilder";
import PublicProposal from "./pages/PublicProposal";
import PublicUploadProposal from "./pages/PublicUploadProposal";
import Rooms from "./pages/Rooms";
import RoomBuilder from "./pages/RoomBuilder";
import PublicRoom from "./pages/PublicRoom";
import { SettingsProvider } from "./lib/settings";
import { ConfirmProvider } from "./components/ConfirmModal";
import BrandingInjector from "./components/BrandingInjector";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex items-center justify-center min-h-screen text-mute">
        <span className="text-sm">loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <SettingsProvider authed={true}>{children}</SettingsProvider>;
}

function OnboardedRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user && user.role === "admin" && !user.onboarding_completed_at) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/setup" element={<Setup />} />
      <Route path="/superadmin/login" element={<SuperadminLogin />} />
      <Route path="/superadmin" element={<SuperadminDashboard />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding" element={<Protected><OnboardingWizard /></Protected>} />
      <Route path="/p/upload/:token" element={<PublicUploadProposal />} />
      <Route path="/p/:tenant_slug/:token" element={<PublicProposal />} />
      <Route path="/p/:token" element={<PublicProposal />} />
      <Route path="/r/:token" element={<PublicRoom />} />
      <Route path="/" element={<Protected><OnboardedRoute><Layout /></OnboardedRoute></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leads" element={<LeadInbox />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="deals" element={<Deals />} />
        <Route path="deals/:id" element={<DealDetail />} />
        <Route path="growth" element={<Deals moduleKind="growth" />} />
        <Route path="growth/:id" element={<DealDetail />} />
        <Route path="moves" element={<Moves />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="deliverables" element={<Deliverables />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="proposals/:id" element={<ProposalBuilder />} />
        <Route path="proposals/:id/sign-builder" element={<SigningPageBuilder />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="rooms/:id" element={<RoomBuilder />} />
        <Route path="insights" element={<Insights />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ConfirmProvider>
            <BrandingInjector />
            <AppRoutes />
            <Toaster position="bottom-right"
              toastOptions={{
                style: {
                  background: "var(--pg-surface)",
                  border: "1px solid var(--pg-border)",
                  color: "var(--pg-text)",
                  borderRadius: 8,
                  fontFamily: "Lora, Georgia, serif",
                  fontWeight: 500,
                  boxShadow: "var(--pg-shadow-card)",
                },
              }} />
          </ConfirmProvider>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
